import { useCallback } from "react";
import { notification } from "antd";
import { User } from "../../user";
import { Status, todoStatuses } from "../../common/status/status";
import { DocumentSetData } from "../interfaces/documentSet";
import { useAppDispatch, useAppSelector } from "../../app";
import {
  fetchDocumentSetsTrigger,
  removeDocumentSet,
  setActiveDocumentSet,
} from "../store/documentSetSlice";
import { useTranslation } from "react-i18next";
import useNavigateWithState from "../../common/navigate/hooks/useNavigateWithState";
import { mapAnnotationsToAnnotatorFormat } from "../../annotation";
import { addAnnotationsForDocument } from "../../annotation/store/annotationSlice";
import { setCategorizationAnnotationsBatch } from "../../metadata/store/metadataSlice";
import {
  configMapIsLoading,
  configMapLoadFailed,
  setConfigMap,
} from "../../configMap/store/configMapSlice";
import { STATUSES_TO_NORMALIZE_ANNOTATIONS } from "../../document/constants";
import { cleanUpTableAnnotationIndexGaps } from "../../annotation/utils/annotationMappers";
import { useMultipleGroupBlocks } from "../../configMap";
import useAnnotationNormalization from "../../configMap/hooks/useAnnotationNormalization";
import { shouldFetchDocumentSetData } from "../utils/helperFunctions";
import {
  useLazyGetDocumentSetDataQuery,
  useLazyUpdateDocumentSetQuery,
} from "../store/documentSetApi";
import { parseReduxQueryError } from "../../common/reduxQuery/helpers";
import { changeIsTokenRefreshing } from "../../user/store/userSlice";
import { getSideAndTableControlAnnotationItems } from "../../annotation/utils/utils";
import { AnnotationItem, HoldReason } from "../../documentTypes/interfaces";
import { Annotation } from "../../annotator/interfaces/annotation";
import { CategorizationAnnotation } from "../../configMap/interfaces/category";
import {
  mapBackToCategorizationAnnotations,
  mapBackToEntityAnnotations,
  updateCategorizationAnnotationsWithNewGroupBlockId,
} from "../../documentTypes/utils/synchronizeAnnotationHelper";
import useCategorizationEvents from "../../metadata/hooks/useCategorizationEvents";
import { convertToEntityAnnotationDtos } from "../../configMap/utils/entityAnnotationUtils";
import { selectConfigMapCategorizations } from "../../configMap/selectors/categorizationLinkSelectors";
import useCategorizationDataFetcher from "../../metadata/hooks/useCategorizationDataFetcher";

const useActiveDocumentSet = () => {
  const { navigateWithState } = useNavigateWithState();
  const { t } = useTranslation("errors");

  const dispatch = useAppDispatch();
  const { activeDocumentSet } = useAppSelector(
    (state) => state.documentSetsReducer,
  );
  const user = useAppSelector((state) => state.userReducer.user);
  const hasConfigMap = useAppSelector((state) =>
    Boolean(state.configMapReducer.configMap),
  );

  const { setInitialMultipleGroupBlocksCount } = useMultipleGroupBlocks();
  const { normalizeEntityAnnotationsInBulk } = useAnnotationNormalization();
  const { triggerOnLoadCategorizationEvents } = useCategorizationEvents();
  const { startCategorizationLookup, fetchCategorizationAnnotationData } =
    useCategorizationDataFetcher();

  const [getDocumentSetData] = useLazyGetDocumentSetDataQuery();

  const [updateDocumentSet] = useLazyUpdateDocumentSetQuery();

  const updateActiveDocumentSet = useCallback(
    (documentSet?: DocumentSetData) => {
      dispatch(setActiveDocumentSet(documentSet));
    },
    [dispatch],
  );

  const fetchDocumentActiveDocumentSet = useCallback(
    async (documentSetId: string, signal: AbortSignal, routeBack = false) => {
      // `hasConfigMap` keeps the guard honest: the set can sit in
      // `documentSetsReducer` while a previous run died before it published the
      // config map, and without this check that run could never be retried.
      if (
        activeDocumentSet?.id === documentSetId &&
        hasConfigMap &&
        !shouldFetchDocumentSetData(activeDocumentSet)
      ) {
        return;
      }

      dispatch(configMapIsLoading());
      let configMapPublished = false;

      try {
        const data = await getDocumentSetData(documentSetId).unwrap();
        if (data) {
          updateActiveDocumentSet(data);

          if (!data.documents) {
            return;
          }

          // Only needs the record ids, so it runs ahead of the mapping.
          const categorizationLookup = startCategorizationLookup(
            data.documents.flatMap(
              (d) => d.categorizationAnnotations?.filter(Boolean) || [],
            ),
          );

          const { documentType, id } = data.documents[0];
          const configMap = documentType?.configMap;

          const configMapCategorizations = configMap
            ? selectConfigMapCategorizations(configMap).map(
                (cwc) => cwc.categorization,
              )
            : [];

          const flatAnnotations = data.documents.flatMap(
            (d) => d.entityAnnotations?.filter(Boolean) || [],
          );
          const flatCategoryAnnotations = data.documents
            .flatMap((d) => d.categorizationAnnotations?.filter(Boolean) || [])
            .map((dca) => ({
              ...dca,
              groupBlockId: dca.groupBlockId ?? undefined,
              index: dca.index,
              originalIndex: dca.index,
            }));

          const updatedCategoryAnnotations =
            updateCategorizationAnnotationsWithNewGroupBlockId(
              [...flatCategoryAnnotations],
              configMap?.groupBlocks,
            );

          const mappedAnnotations = mapAnnotationsToAnnotatorFormat(
            flatAnnotations,
            configMap,
          );

          const annotationItems: Array<AnnotationItem> = [
            ...(mappedAnnotations.map((a) => ({
              ...a,
              isEntity: true,
            })) as Array<AnnotationItem>),
            ...((updatedCategoryAnnotations.map((a) => ({
              ...a,
              isEntity: false,
            })) ?? []) as Array<AnnotationItem>),
          ];

          const {
            sideControlAnnotations: sideControlAnnotationItems,
            tableControlAnnotations: tableControlAnnotationItems,
          } = getSideAndTableControlAnnotationItems(annotationItems);

          const convertedTableControlsAnnotations =
            cleanUpTableAnnotationIndexGaps(tableControlAnnotationItems);

          const annotationsResult = sideControlAnnotationItems.concat(
            convertedTableControlsAnnotations,
          );

          const entityAnnotations: Array<Annotation> =
            mapBackToEntityAnnotations(annotationsResult);
          const categorizationAnnotations: Array<CategorizationAnnotation> =
            mapBackToCategorizationAnnotations(annotationsResult);

          // The table controls size themselves once, when the config map gives
          // them a group block, from the rows in the store at that moment. So
          // the rows and their counts go in first. The counts only read
          // categorization indices, which the lookup below does not change.
          dispatch(
            addAnnotationsForDocument({
              documentId: id,
              annotations: entityAnnotations,
            }),
          );
          setInitialMultipleGroupBlocksCount(
            configMap!,
            entityAnnotations,
            categorizationAnnotations,
          );

          // Nothing below depends on the categorization lookup, so publish the
          // config map before it. A slow master data call must not hold the
          // side panel on its skeleton.
          dispatch(setConfigMap(configMap!));
          dispatch(changeIsTokenRefreshing(false));
          configMapPublished = true;

          const updatedCategorizationAnnotations =
            await fetchCategorizationAnnotationData(
              categorizationAnnotations,
              categorizationLookup,
            );

          dispatch(
            setCategorizationAnnotationsBatch(updatedCategorizationAnnotations),
          );
          triggerOnLoadCategorizationEvents(
            configMapCategorizations,
            updatedCategorizationAnnotations,
            mappedAnnotations,
            data.input.attribute,
            id,
            data,
          );

          if (STATUSES_TO_NORMALIZE_ANNOTATIONS.includes(data.status)) {
            const annotationsToNormalize =
              convertToEntityAnnotationDtos(entityAnnotations);
            normalizeEntityAnnotationsInBulk(
              annotationsToNormalize,
              true,
              updatedCategorizationAnnotations,
              configMap,
            );
          }
        }
      } catch (error: unknown) {
        if (signal.aborted) {
          return;
        }

        // The awaited calls above reject with an RTK Query error object. The
        // cast keeps the original direct `.status` access (which throws on a
        // nullish error) rather than silently changing behaviour for values
        // that cannot occur here.
        const status = (error as { status?: number }).status;

        if (status === 400 && routeBack) {
          notification.error({
            title: t("documentSetBadRequest"),
          });
          navigateWithState("/");
          return;
        } else if (status === 403 && routeBack) {
          notification.error({
            title: t("documentSetUnauthorized"),
          });
          navigateWithState("/");
          return;
        } else if (status === 404 && routeBack) {
          notification.error({
            title: t("documentSetNotFound"),
          });
          navigateWithState("/");
          return;
        } else if (routeBack) {
          notification.error({
            title: t("documentSetOtherError"),
          });
          navigateWithState("/");
          return;
        } else {
          notification.error({
            title: t("documentSetOtherError"),
          });
        }

        console.error(parseReduxQueryError(error));
      } finally {
        // Every exit above that never reaches `setConfigMap` would otherwise
        // leave `loading` on forever. An aborted run is skipped: whoever
        // aborted it owns the state now.
        if (!configMapPublished && !signal.aborted) {
          dispatch(configMapLoadFailed());
        }
      }
    },
    [
      activeDocumentSet,
      hasConfigMap,
      dispatch,
      updateActiveDocumentSet,
      setInitialMultipleGroupBlocksCount,
      normalizeEntityAnnotationsInBulk,
      t,
      navigateWithState,
      getDocumentSetData,
      triggerOnLoadCategorizationEvents,
      startCategorizationLookup,
      fetchCategorizationAnnotationData,
    ],
  );

  const updateAssigneeAndStatus = useCallback(
    async (
      documentSet: DocumentSetData,
      newStatus?: Status,
      assignee?: User,
      shouldUpdateActiveDocumentSet = true,
      holdReason?: HoldReason,
    ): Promise<DocumentSetData> => {
      const newAssigneeDocumentSet = { ...documentSet };

      if (newStatus) {
        newAssigneeDocumentSet.status = newStatus;
        // Mirrors the backend: the reason only lives on a held set.
        newAssigneeDocumentSet.holdReason =
          newStatus === Status.OnHold ? holdReason : undefined;
      }
      newAssigneeDocumentSet.assignee = assignee;

      await updateDocumentSet({
        documentSet,
        newStatus,
        assignee,
        holdReasonId: holdReason?.id,
      }).unwrap();
      if (newAssigneeDocumentSet && shouldUpdateActiveDocumentSet) {
        updateActiveDocumentSet(newAssigneeDocumentSet);
      }

      return newAssigneeDocumentSet;
    },
    [updateActiveDocumentSet, updateDocumentSet],
  );

  const updateAssigneeAndStatusWithExceptionHandling = useCallback(
    async (
      documentSet: DocumentSetData,
      newStatus?: Status,
      assignee?: User,
      shouldUpdateActiveDocumentSet = true,
      holdReason?: HoldReason,
    ): Promise<DocumentSetData> => {
      try {
        return await updateAssigneeAndStatus(
          documentSet,
          newStatus,
          assignee,
          shouldUpdateActiveDocumentSet,
          holdReason,
        );
      } catch (e: unknown) {
        // Handle 409 conflict when document set is being processed
        // Check both e.status and e.data?.status for RTK Query errors
        const error = e as
          { status?: number; data?: { status?: number } } | null | undefined;
        const errorStatus = error?.status || error?.data?.status;
        if (errorStatus === 409) {
          notification.error({
            title: "Error",
            description: t("documentSetProcessing"),
            duration: 5,
          });
          navigateWithState("/");
        }
        return documentSet;
      }
    },
    [updateAssigneeAndStatus, t, navigateWithState],
  );

  const assignUserToActiveDocument = useCallback(async () => {
    if (!activeDocumentSet?.assignee) {
      await updateAssigneeAndStatusWithExceptionHandling(
        activeDocumentSet!,
        undefined,
        user!,
      );
    } else if (
      activeDocumentSet?.assignee?.id !== user?.id &&
      todoStatuses.find((status) => status === activeDocumentSet?.status) !==
        undefined
    ) {
      navigateWithState("/");
    }
  }, [
    updateAssigneeAndStatusWithExceptionHandling,
    user,
    activeDocumentSet,
    navigateWithState,
  ]);

  const handleRemoveDocumentSet = useCallback(
    async (documentSet: DocumentSetData) => {
      await updateAssigneeAndStatusWithExceptionHandling(
        documentSet,
        Status.Remove,
        user!,
        false,
      );
      dispatch(removeDocumentSet(documentSet));
      dispatch(fetchDocumentSetsTrigger());
    },
    [dispatch, updateAssigneeAndStatusWithExceptionHandling, user],
  );

  const handleRestoreDocumentSet = useCallback(
    async (documentSet: DocumentSetData) => {
      const status = documentSet.metadata?.preRemoveStatus || Status.ToDo;

      await updateAssigneeAndStatusWithExceptionHandling(
        documentSet,
        status,
        undefined,
        false,
      );
      dispatch(removeDocumentSet(documentSet));
      dispatch(fetchDocumentSetsTrigger());
    },
    [dispatch, updateAssigneeAndStatusWithExceptionHandling],
  );

  return {
    activeDocumentSet,
    updateActiveDocumentSet,
    assignUserToActiveDocument,
    updateAssigneeAndStatusWithExceptionHandling,
    updateAssigneeAndStatus,
    fetchDocumentActiveDocumentSet,
    handleRemoveDocumentSet,
    handleRestoreDocumentSet,
  };
};

export default useActiveDocumentSet;
